// frontend/src/pages/dashboards/shareState.js
// Phase 4 step 14c-3: the Share dialog's rules, kept pure so they are tested
// without a browser (shareState.test.js). The dialog (ShareDialog.jsx) only
// draws what these return. The People and level rules (14d-3) are in
// accessState.js.
//
// The server's listing (backend app/dashboards/shares.py `_listing`) is
//   { dashboard_id, access: 'org' | 'restricted', max_active, expiry_choices,
//     filters: [{ id, label, kind }], charts: [{ id, title, visibility }],
//     shares: [share], share? }
// and a share is
//   { id, label, status: 'active' | 'expired' | 'revoked' | 'restricted', created_by,
//     created_at, expires_at, revoked_at, revoked_by, allow_embed, filter_ids,
//     view_count, last_viewed_at, url, embed_code }   (url / embed_code only while active)
// 'restricted' (14d-1c): the link would work, but the dashboard is restricted
// and a restricted dashboard is never public, so it opens nothing.
// listingFromServer keeps only what has that shape, so the dialog never draws
// or copies something it did not expect.
import { cleanText } from './dashboardState';

export const MAX_LABEL = 60;               // shares.MAX_LABEL
export const DEFAULT_MAX_ACTIVE = 10;      // shares.MAX_ACTIVE_SHARES
const DAY_CHOICES = [7, 30, 90];           // shares.EXPIRY_CHOICES
const STATUSES = ['active', 'expired', 'revoked', 'restricted'];
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The expiry select's options. 'date' reveals a date input. */
export const EXPIRY_OPTIONS = [
  { id: 'never', label: 'Never' },
  { id: '7', label: 'In 7 days' },
  { id: '30', label: 'In 30 days' },
  { id: '90', label: 'In 90 days' },
  { id: 'date', label: 'On a date' },
];

export const STATUS_LABELS = { active: 'Active', expired: 'Expired', revoked: 'Revoked', restricted: 'Off while restricted' };

// --- the server's listing -----------------------------------------------------------

const text = (v) => (typeof v === 'string' ? v : null);
const count = (v) => (Number.isInteger(v) && v >= 0 ? v : 0);
/** A link we are willing to show and copy: http(s) on the public dashboard path, nothing else. */
const publicUrl = (v) => (typeof v === 'string' && /^https?:\/\/[^\s"'<>]+\/public\/dashboards\/[A-Za-z0-9_-]+$/.test(v) ? v : null);

export function shareFromServer(s) {
  if (!s || !Number.isInteger(s.id) || s.id <= 0) return null;
  const status = STATUSES.includes(s.status) ? s.status : 'revoked';      // an unknown status is treated as not working
  const url = status === 'active' ? publicUrl(s.url) : null;
  return {
    id: s.id,
    label: text(s.label) || '',
    status,
    createdBy: text(s.created_by),
    createdAt: text(s.created_at),
    expiresAt: text(s.expires_at),
    revokedAt: text(s.revoked_at),
    revokedBy: text(s.revoked_by),
    allowEmbed: s.allow_embed === true,
    filterIds: Array.isArray(s.filter_ids) ? s.filter_ids.filter((x) => typeof x === 'string') : null,
    viewCount: count(s.view_count),
    lastViewedAt: text(s.last_viewed_at),
    url,
    embedCode: url && s.allow_embed === true && typeof s.embed_code === 'string' ? s.embed_code : null,
  };
}

export function listingFromServer(body) {
  const b = body && typeof body === 'object' ? body : {};
  const filters = (Array.isArray(b.filters) ? b.filters : [])
    .filter((f) => f && typeof f.id === 'string' && f.id)
    .map((f) => ({ id: f.id, label: text(f.label) || 'Filter', kind: text(f.kind) || 'values' }));
  const charts = (Array.isArray(b.charts) ? b.charts : [])
    .filter((c) => c && Number.isInteger(c.id) && c.id > 0)
    .map((c) => ({ id: c.id, title: text(c.title) || 'Untitled chart', visibility: c.visibility === 'public' ? 'public' : 'private' }));
  const shares = (Array.isArray(b.shares) ? b.shares : []).map(shareFromServer).filter(Boolean);
  const maxActive = Number.isInteger(b.max_active) && b.max_active > 0 ? b.max_active : DEFAULT_MAX_ACTIVE;
  const access = b.access === 'restricted' ? 'restricted' : 'org';
  return { access, filters, charts, shares, maxActive, created: b.share ? shareFromServer(b.share) : null };
}

export const activeShares = (shares) => (shares || []).filter((s) => s.status === 'active');
export const pastShares = (shares) => (shares || []).filter((s) => s.status !== 'active');
export const canMakeMore = (listing) => activeShares(listing.shares).length < listing.maxActive;
/** A restricted dashboard is never public (14d-1b): no new link, and the section says why. */
export const linksAllowed = (listing) => Boolean(listing) && listing.access !== 'restricted';
export const privateCharts = (charts) => (charts || []).filter((c) => c.visibility !== 'public');

// --- a new link ---------------------------------------------------------------------------

/** Today's date in UTC as YYYY-MM-DD: a link "on a date" works through the end of that day, UTC (the server's rule). */
export function todayUtc(now = Date.now()) {
  return new Date(now).toISOString().slice(0, 10);
}

/** A new link's form as the dialog opens it: no label, never expires, not embeddable, every filter changeable. */
export function newForm(filters = []) {
  return { label: '', expiry: 'never', date: '', allowEmbed: false, filterIds: filters.map((f) => f.id) };
}

/** What is wrong with the form, or null. */
export function formProblem(form, now = Date.now()) {
  if (cleanText(form.label, MAX_LABEL + 1, { oneLine: true }).length > MAX_LABEL) return `Keep the label under ${MAX_LABEL} characters.`;
  if (form.expiry === 'date') {
    if (!ISO_DAY.test(form.date || '')) return 'Pick the last day the link works.';
    const d = new Date(`${form.date}T00:00:00Z`);
    if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== form.date) return 'Pick the last day the link works.';
    if (form.date < todayUtc(now)) return 'Pick today or a later day.';
  }
  return null;
}

/**
 * The POST body for a new link. `filter_ids` is always the list ticked: a
 * filter added to the dashboard later stays at its starting value on this
 * link (sending null would let viewers change filters nobody chose to show).
 */
export function createBody(form, filters = []) {
  const known = new Set(filters.map((f) => f.id));
  const body = {
    allow_embed: Boolean(form.allowEmbed),
    filter_ids: filters.map((f) => f.id).filter((id) => (form.filterIds || []).includes(id) && known.has(id)),
  };
  const label = cleanText(form.label, MAX_LABEL, { oneLine: true });
  if (label) body.label = label;
  if (DAY_CHOICES.includes(Number(form.expiry))) body.expires_in_days = Number(form.expiry);
  else if (form.expiry === 'date' && form.date) body.expires_at = form.date;
  return body;
}

export function toggleFilter(form, id) {
  const ids = form.filterIds || [];
  return { ...form, filterIds: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
}

/** The note under a filter in the checklist (design 10.5). */
export function filterNote(filter) {
  return filter && filter.kind === 'values' ? 'Viewers can see every value of this field.' : '';
}

// --- words ------------------------------------------------------------------------------------

export function agoText(iso, now = Date.now()) {
  const then = Date.parse(iso || '');
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) { const h = Math.floor(s / 3600); return `${h} hour${h === 1 ? '' : 's'} ago`; }
  if (s < 86400 * 30) { const d = Math.floor(s / 86400); return `${d} day${d === 1 ? '' : 's'} ago`; }
  return dayText(iso);
}

export function dayText(iso) {
  const t = Date.parse(iso || '');
  return Number.isNaN(t) ? '' : new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The lines under a link in the list: who made it and when, its expiry or end, how often it was opened. */
export function shareLines(share, now = Date.now()) {
  const made = `Made ${agoText(share.createdAt, now) || 'earlier'}${share.createdBy ? ` by ${share.createdBy}` : ''}`;
  let end;
  if (share.status === 'revoked') end = `Revoked ${agoText(share.revokedAt, now) || ''}${share.revokedBy ? ` by ${share.revokedBy}` : ''}`.replace(/\s+$/, '');
  else if (share.status === 'expired') end = `Expired ${dayText(share.expiresAt)}`.trim();
  else if (share.status === 'restricted') end = 'Opens nothing while only specific people can open the dashboard';
  else end = share.expiresAt ? `Works until ${dayText(share.expiresAt)}` : 'Never expires';
  const opened = share.viewCount === 0 ? 'Not opened yet'
    : `Opened ${share.viewCount.toLocaleString()} time${share.viewCount === 1 ? '' : 's'}${share.lastViewedAt ? `, last ${agoText(share.lastViewedAt, now)}` : ''}`;
  return [made, end, opened];
}

/** What a link lets viewers change: "Viewers can change: Period, Department" / "Viewers can't change any filter". */
export function filtersText(share, filters) {
  if (!filters.length) return '';
  const ids = share.filterIds === null ? filters.map((f) => f.id) : share.filterIds;
  const names = filters.filter((f) => ids.includes(f.id)).map((f) => f.label);
  return names.length ? `Viewers can change: ${names.join(', ')}` : "Viewers can't change any filter";
}

/** The warning above "Make link" (design 10.5). */
export function exposureText(charts) {
  const n = privateCharts(charts).length;
  const base = 'Anyone with the link sees every tile on this dashboard and its data, without signing in.';
  if (!n) return base;
  return `${base} That includes ${n} private chart${n === 1 ? '' : 's'}, which ${n === 1 ? 'is' : 'are'} otherwise visible only in your organization.`;
}

/** A failed list, create or revoke, in words; the server's own message only for the typed codes that carry one for people. */
export function shareFailureMessage(err, action = 'load') {
  if (err && err.status === 403) return "You don't have permission to share dashboards.";
  if (err && err.status === 404) return 'This dashboard no longer exists.';
  if (err && err.code === 'SHARE_LIMIT' && err.message) return err.message;     // a whole sentence: "… Revoke one first."
  if (err && err.code === 'DASHBOARD_RESTRICTED') return "Only specific people can open this dashboard, so it can't have public links."
  if (err && err.code === 'INVALID_SHARE') {
    // The server's message names a rule ("must be in the future"), the path says which field.
    if (err.path === '$.expires_at' || err.path === '$.expires_in_days') return 'Pick today or a later day for the link to stop working.';
    if (err.path === '$.label') return `Use a one-line label under ${MAX_LABEL} characters.`;
    if (err.path && err.path.startsWith('$.filter_ids')) return 'A filter changed while this was open. Close the dialog and try again.';
    return "The link couldn't be made. Check what you entered and try again.";
  }
  if (action === 'create') return "The link couldn't be made. Try again.";
  if (action === 'revoke') return "The link couldn't be revoked. Try again.";
  return "The links couldn't be loaded. Try again.";
}
